import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSettings, FiRefreshCw, FiDatabase, FiCheckCircle, FiXCircle, FiClock } from 'react-icons/fi';
import './NeoFeeder.css';

const NeoFeeder = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingCourses, setSyncingCourses] = useState(false);
  const [syncingLecturers, setSyncingLecturers] = useState(false);
  const [syncingBiodata, setSyncingBiodata] = useState(false);
  const [syncingKRS, setSyncingKRS] = useState(false);
  const [syncingKHS, setSyncingKHS] = useState(false);
  const [syncingAKM, setSyncingAKM] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [showSyncFilter, setShowSyncFilter] = useState(false);
  const [showCourseSyncFilter, setShowCourseSyncFilter] = useState(false);
  const [showLecturerSyncFilter, setShowLecturerSyncFilter] = useState(false);
  const [showBiodataSyncFilter, setShowBiodataSyncFilter] = useState(false);
  const [showKRSSyncFilter, setShowKRSSyncFilter] = useState(false);
  const [showKHSSyncFilter, setShowKHSSyncFilter] = useState(false);
  const [showAKMSyncFilter, setShowAKMSyncFilter] = useState(false);
  const [prodiList, setProdiList] = useState([]);
  const [formData, setFormData] = useState({
    url: '',
    username: '',
    password: ''
  });
  const [syncFilter, setSyncFilter] = useState({
    tahunMasuk: '',
    programStudi: '',
    limit: 100,
    syncMode: 'upsert'
  });
  const [courseSyncFilter, setCourseSyncFilter] = useState({
    programStudi: '',
    tahunAjaran: '',
    limit: 100,
    syncMode: 'upsert'
  });
  const [lecturerSyncFilter, setLecturerSyncFilter] = useState({
    limit: 100,
    syncMode: 'upsert'
  });
  const [biodataSyncFilter, setBiodataSyncFilter] = useState({
    id_registrasi_mahasiswa: '',
    limit: 100,
    syncMode: 'upsert'
  });
  const [krsSyncFilter, setKrsSyncFilter] = useState({
    id_semester: '',
    id_registrasi_mahasiswa: '',
    limit: 100,
    syncMode: 'upsert'
  });
  const [khsSyncFilter, setKhsSyncFilter] = useState({
    id_semester: '',
    id_prodi: '',
    nim: '',
    syncMode: 'upsert'
  });
  const [akmSyncFilter, setAkmSyncFilter] = useState({
    id_semester: '',
    id_prodi: '',
    nim: '',
    limit: 500,
    limitType: 'custom', // 'all' or 'custom'
    syncMode: 'upsert'
  });

  useEffect(() => {
    fetchData();
    fetchProdiList();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, historyRes] = await Promise.all([
        axios.get('/api/neofeeder/config'),
        axios.get('/api/neofeeder/stats'),
        axios.get('/api/neofeeder/history?limit=10')
      ]);

      setConfig(configRes.data.data);
      setStats(statsRes.data.data);
      setHistory(historyRes.data.data);

      if (configRes.data.data) {
        setFormData({
          url: configRes.data.data.url || '',
          username: configRes.data.data.username || '',
          password: configRes.data.data.password || '' // Ambil password dari config jika ada
        });
      }
    } catch (error) {
      toast.error('Gagal mengambil data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProdiList = async () => {
    try {
      const res = await axios.get('/api/neofeeder/prodi');
      setProdiList(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch prodi list:', error);
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put('/api/neofeeder/config', formData);
      toast.success('Konfigurasi berhasil disimpan');
      setShowConfigForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menyimpan konfigurasi');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post('/api/neofeeder/test');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Koneksi gagal');
    } finally {
      setTesting(false);
    }
  };

  const handleSyncStudents = async () => {
    setSyncing(true);
    try {
      const res = await axios.post('/api/neofeeder/sync/students', syncFilter);
      toast.success(res.data.message);
      setShowSyncFilter(false);
      setSyncFilter({ tahunMasuk: '', programStudi: '', limit: 100, syncMode: 'upsert' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sinkronisasi gagal');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncCourses = async () => {
    setSyncingCourses(true);
    try {
      const res = await axios.post('/api/neofeeder/sync/courses', courseSyncFilter);
      toast.success(res.data.message);
      setShowCourseSyncFilter(false);
      setCourseSyncFilter({ programStudi: '', tahunAjaran: '', limit: 100, syncMode: 'upsert' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sinkronisasi mata kuliah gagal');
    } finally {
      setSyncingCourses(false);
    }
  };

  const handleSyncLecturers = async () => {
    setSyncingLecturers(true);
    try {
      const res = await axios.post('/api/neofeeder/sync/lecturers', lecturerSyncFilter);
      toast.success(res.data.message);
      setShowLecturerSyncFilter(false);
      setLecturerSyncFilter({ limit: 100, syncMode: 'upsert' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sinkronisasi dosen gagal');
    } finally {
      setSyncingLecturers(false);
    }
  };

  const handleSyncBiodata = async () => {
    setSyncingBiodata(true);
    try {
      const res = await axios.post('/api/neofeeder/sync/biodata-mahasiswa', biodataSyncFilter);
      toast.success(res.data.message);
      setShowBiodataSyncFilter(false);
      setBiodataSyncFilter({ id_registrasi_mahasiswa: '', limit: 100, syncMode: 'upsert' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sinkronisasi biodata mahasiswa gagal');
    } finally {
      setSyncingBiodata(false);
    }
  };

  const handleSyncKRS = async () => {
    setSyncingKRS(true);
    try {
      console.log('Sending KRS sync request with filter:', krsSyncFilter);
      const res = await axios.post('/api/neofeeder/sync/krs', krsSyncFilter);
      console.log('KRS sync response:', res.data);
      toast.success(res.data.message);
      setShowKRSSyncFilter(false);
      setKrsSyncFilter({ id_semester: '', id_registrasi_mahasiswa: '', limit: 100, syncMode: 'upsert' });
      fetchData();
    } catch (error) {
      console.error('KRS sync error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Sinkronisasi KRS gagal');
    } finally {
      setSyncingKRS(false);
    }
  };

  const handleSyncKHS = async () => {
    setSyncingKHS(true);
    try {
      console.log('Sending KHS sync request with filter:', khsSyncFilter);
      const res = await axios.post('/api/neofeeder/sync/khs', khsSyncFilter);
      console.log('KHS sync response:', res.data);
      toast.success(res.data.message);
      setShowKHSSyncFilter(false);
      setKhsSyncFilter({ id_semester: '', id_prodi: '', nim: '', syncMode: 'upsert' });
      fetchData();
    } catch (error) {
      console.error('KHS sync error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Sinkronisasi KHS gagal');
    } finally {
      setSyncingKHS(false);
    }
  };

  const handleSyncAKM = async () => {
    setSyncingAKM(true);
    try {
      console.log('Sending AKM sync request with filter:', akmSyncFilter);
      
      // Prepare request data
      const requestData = {
        id_semester: akmSyncFilter.id_semester,
        id_prodi: akmSyncFilter.id_prodi,
        nim: akmSyncFilter.nim,
        syncMode: akmSyncFilter.syncMode,
        limit: akmSyncFilter.limitType === 'all' ? 999999 : parseInt(akmSyncFilter.limit)
      };
      
      const res = await axios.post('/api/neofeeder/sync/akm', requestData);
      console.log('AKM sync response:', res.data);
      toast.success(res.data.message);
      setShowAKMSyncFilter(false);
      setAkmSyncFilter({ id_semester: '', id_prodi: '', nim: '', limit: 500, limitType: 'custom', syncMode: 'upsert' });
      fetchData();
    } catch (error) {
      console.error('AKM sync error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Sinkronisasi AKM gagal');
    } finally {
      setSyncingAKM(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FiCheckCircle color="#10b981" />;
      case 'failed':
        return <FiXCircle color="#ef4444" />;
      case 'in-progress':
        return <FiClock color="#f59e0b" />;
      default:
        return <FiClock color="#6b7280" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'completed': 'table-badge-success',
      'failed': 'table-badge-danger',
      'in-progress': 'table-badge-warning',
      'pending': 'table-badge-info'
    };
    return badges[status] || 'table-badge-info';
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="neofeeder-page">
      <div className="page-header">
        <div>
          <h2>Integrasi NEO Feeder</h2>
          <p>Sinkronisasi data dengan Pangkalan Data Pendidikan Tinggi (PDDIKTI)</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowConfigForm(!showConfigForm)}
        >
          <FiSettings /> Konfigurasi
        </button>
      </div>

      {/* Configuration Form */}
      {showConfigForm && (
        <div className="card">
          <h3>Konfigurasi NEO Feeder</h3>
          <form onSubmit={handleConfigSubmit}>
            <div className="form-group">
              <label>URL NEO Feeder</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="form-control"
                placeholder="http://localhost:8100/ws/live2.php"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="form-control"
                  placeholder="Kosongkan jika tidak diubah"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowConfigForm(false)}>
                Batal
              </button>
              <button type="submit" className="btn btn-primary">
                Simpan Konfigurasi
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#4f46e5' }}>
            <FiDatabase />
          </div>
          <div className="stat-details">
            <h3>{stats?.total || 0}</h3>
            <p>Total Sinkronisasi</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <FiCheckCircle />
          </div>
          <div className="stat-details">
            <h3>{stats?.success || 0}</h3>
            <p>Berhasil</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#ef4444' }}>
            <FiXCircle />
          </div>
          <div className="stat-details">
            <h3>{stats?.failed || 0}</h3>
            <p>Gagal</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f59e0b' }}>
            <FiClock />
          </div>
          <div className="stat-details">
            <h3>
              {stats?.lastSync 
                ? new Date(stats.lastSync.createdAt).toLocaleDateString('id-ID')
                : '-'
              }
            </h3>
            <p>Sinkronisasi Terakhir</p>
          </div>
        </div>
      </div>

      {/* Sync Filter Modal */}
      {showSyncFilter && (
        <div className="card">
          <h3>Filter Sinkronisasi Mahasiswa</h3>
          <p className="text-muted">Pilih kriteria untuk sinkronisasi data mahasiswa dari NEO Feeder</p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSyncStudents(); }}>
            <div className="form-row">
              <div className="form-group">
                <label>Tahun Masuk (Opsional)</label>
                <input
                  type="text"
                  value={syncFilter.tahunMasuk}
                  onChange={(e) => setSyncFilter({ ...syncFilter, tahunMasuk: e.target.value })}
                  className="form-control"
                  placeholder="Contoh: 2020, 2021, 2022"
                />
                <small className="text-muted">Kosongkan untuk semua tahun</small>
              </div>

              <div className="form-group">
                <label>Program Studi (Opsional)</label>
                <select
                  value={syncFilter.programStudi}
                  onChange={(e) => setSyncFilter({ ...syncFilter, programStudi: e.target.value })}
                  className="form-control"
                >
                  <option value="">Semua Program Studi</option>
                  {prodiList.map(prodi => (
                    <option key={prodi.id} value={prodi.id}>
                      {prodi.jenjang} - {prodi.nama}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  value={syncFilter.syncMode}
                  onChange={(e) => setSyncFilter({ ...syncFilter, syncMode: e.target.value })}
                  className="form-control"
                  required
                >
                  <option value="upsert">UPSERT - Update jika ada, Insert jika tidak ada</option>
                  <option value="insert">INSERT - Hanya tambah data baru, skip yang sudah ada</option>
                  <option value="update">UPDATE - Hanya update data yang sudah ada</option>
                </select>
                <small className="text-muted">
                  {syncFilter.syncMode === 'upsert' && '✓ Cocok untuk sinkronisasi umum (default)'}
                  {syncFilter.syncMode === 'insert' && '✓ Cocok untuk menambah data baru saja'}
                  {syncFilter.syncMode === 'update' && '✓ Cocok untuk update data yang sudah ada'}
                </small>
              </div>

              <div className="form-group">
                <label>Maksimal Data</label>
                <input
                  type="number"
                  value={syncFilter.limit}
                  onChange={(e) => setSyncFilter({ ...syncFilter, limit: e.target.value })}
                  className="form-control"
                  min="1"
                  max="1000"
                  required
                />
                <small className="text-muted">Maksimal 1000 data per sinkronisasi</small>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => {
                  setShowSyncFilter(false);
                  setSyncFilter({ tahunMasuk: '', programStudi: '', limit: 100, syncMode: 'upsert' });
                }}
              >
                Batal
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={syncing}
              >
                {syncing ? 'Menyinkronkan...' : 'Mulai Sinkronisasi'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Course Sync Filter Modal */}
      {showCourseSyncFilter && (
        <div className="card">
          <h3>Filter Sinkronisasi Mata Kuliah</h3>
          <p className="text-muted">Pilih kriteria untuk sinkronisasi data mata kuliah dari NEO Feeder</p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSyncCourses(); }}>
            <div className="form-row">
              <div className="form-group">
                <label>Program Studi (Opsional)</label>
                <select
                  value={courseSyncFilter.programStudi}
                  onChange={(e) => setCourseSyncFilter({ ...courseSyncFilter, programStudi: e.target.value })}
                  className="form-control"
                >
                  <option value="">Semua Program Studi</option>
                  {prodiList.map(prodi => (
                    <option key={prodi.id} value={prodi.id}>
                      {prodi.jenjang} - {prodi.nama}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tahun Ajaran (Opsional)</label>
                <select
                  value={courseSyncFilter.tahunAjaran}
                  onChange={(e) => setCourseSyncFilter({ ...courseSyncFilter, tahunAjaran: e.target.value })}
                  className="form-control"
                >
                  <option value="">Semua Tahun Ajaran</option>
                  {(() => {
                    const years = [];
                    const currentYear = new Date().getFullYear();
                    for (let year = 2019; year <= currentYear; year++) {
                      const nextYear = year + 1;
                      years.push(
                        <option key={year} value={`${year}/${nextYear}`}>
                          {year}/{nextYear}
                        </option>
                      );
                    }
                    return years;
                  })()}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  value={courseSyncFilter.syncMode}
                  onChange={(e) => setCourseSyncFilter({ ...courseSyncFilter, syncMode: e.target.value })}
                  className="form-control"
                  required
                >
                  <option value="upsert">UPSERT - Update jika ada, Insert jika tidak ada</option>
                  <option value="insert">INSERT - Hanya tambah data baru, skip yang sudah ada</option>
                  <option value="update">UPDATE - Hanya update data yang sudah ada</option>
                </select>
                <small className="text-muted">
                  {courseSyncFilter.syncMode === 'upsert' && '✓ Cocok untuk sinkronisasi umum (default)'}
                  {courseSyncFilter.syncMode === 'insert' && '✓ Cocok untuk menambah mata kuliah baru saja'}
                  {courseSyncFilter.syncMode === 'update' && '✓ Cocok untuk update mata kuliah yang sudah ada'}
                </small>
              </div>

              <div className="form-group">
                <label>Maksimal Data</label>
                <input
                  type="number"
                  value={courseSyncFilter.limit}
                  onChange={(e) => setCourseSyncFilter({ ...courseSyncFilter, limit: e.target.value })}
                  className="form-control"
                  min="1"
                  max="1000"
                  required
                />
                <small className="text-muted">Maksimal 1000 data per sinkronisasi</small>
              </div>
            </div>

            <div className="alert alert-info">
              <strong>ℹ️ Catatan:</strong> 
              <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                <li>Filter tahun ajaran menampilkan dari <strong>2019/2020</strong> hingga tahun ajaran saat ini</li>
                <li>Backend tetap memfilter data dengan tahun ajaran <strong>≥ 2019/2020</strong></li>
              </ul>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => {
                  setShowCourseSyncFilter(false);
                  setCourseSyncFilter({ programStudi: '', tahunAjaran: '', limit: 100, syncMode: 'upsert' });
                }}
              >
                Batal
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={syncingCourses}
              >
                {syncingCourses ? 'Menyinkronkan...' : 'Mulai Sinkronisasi'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lecturer Sync Filter Modal */}
      {showLecturerSyncFilter && (
        <div className="card">
          <h3>Filter Sinkronisasi Dosen</h3>
          <p className="text-muted">Sinkronisasi data dosen dari NEO Feeder</p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSyncLecturers(); }}>
            <div className="form-row">
              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  value={lecturerSyncFilter.syncMode}
                  onChange={(e) => setLecturerSyncFilter({ ...lecturerSyncFilter, syncMode: e.target.value })}
                  className="form-control"
                  required
                >
                  <option value="upsert">UPSERT - Update jika ada, Insert jika tidak ada</option>
                  <option value="insert">INSERT - Hanya tambah data baru, skip yang sudah ada</option>
                  <option value="update">UPDATE - Hanya update data yang sudah ada</option>
                </select>
                <small className="text-muted">
                  {lecturerSyncFilter.syncMode === 'upsert' && '✓ Cocok untuk sinkronisasi umum (default)'}
                  {lecturerSyncFilter.syncMode === 'insert' && '✓ Cocok untuk menambah dosen baru saja'}
                  {lecturerSyncFilter.syncMode === 'update' && '✓ Cocok untuk update dosen yang sudah ada'}
                </small>
              </div>

              <div className="form-group">
                <label>Maksimal Data</label>
                <input
                  type="number"
                  value={lecturerSyncFilter.limit}
                  onChange={(e) => setLecturerSyncFilter({ ...lecturerSyncFilter, limit: e.target.value })}
                  className="form-control"
                  min="1"
                  max="1000"
                  required
                />
                <small className="text-muted">Maksimal 1000 data per sinkronisasi</small>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => {
                  setShowLecturerSyncFilter(false);
                  setLecturerSyncFilter({ limit: 100, syncMode: 'upsert' });
                }}
              >
                Batal
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={syncingLecturers}
              >
                {syncingLecturers ? 'Menyinkronkan...' : 'Mulai Sinkronisasi'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Action Buttons */}
      <div className="card">
        <h3>Aksi Sinkronisasi</h3>
        <div className="action-grid">
          <button 
            className="action-btn"
            onClick={handleTestConnection}
            disabled={testing || !config?.url}
          >
            <FiCheckCircle size={24} />
            <span>{testing ? 'Testing...' : 'Test Koneksi'}</span>
            <p>Cek koneksi ke NEO Feeder</p>
          </button>

          <button 
            className="action-btn"
            onClick={() => setShowSyncFilter(true)}
            disabled={syncing || !config?.url}
          >
            <FiRefreshCw size={24} />
            <span>{syncing ? 'Syncing...' : 'Sync Mahasiswa'}</span>
            <p>Sinkronkan data mahasiswa</p>
          </button>

          <button 
            className="action-btn"
            onClick={() => setShowBiodataSyncFilter(true)}
            disabled={syncingBiodata || !config?.url}
          >
            <FiDatabase size={24} />
            <span>{syncingBiodata ? 'Syncing...' : 'Sync Biodata Mahasiswa'}</span>
            <p>GetBiodataMahasiswa</p>
          </button>

          <button 
            className="action-btn"
            onClick={() => setShowLecturerSyncFilter(true)}
            disabled={syncingLecturers || !config?.url}
          >
            <FiDatabase size={24} />
            <span>{syncingLecturers ? 'Syncing...' : 'Sync Dosen'}</span>
            <p>Sinkronkan data dosen</p>
          </button>

          <button 
            className="action-btn"
            onClick={() => setShowCourseSyncFilter(true)}
            disabled={syncingCourses || !config?.url}
          >
            <FiDatabase size={24} />
            <span>{syncingCourses ? 'Syncing...' : 'Sync Mata Kuliah'}</span>
            <p>Sinkronkan data mata kuliah</p>
          </button>

          <button 
            className="action-btn"
            onClick={() => setShowKRSSyncFilter(true)}
            disabled={syncingKRS || !config?.url}
          >
            <FiDatabase size={24} />
            <span>{syncingKRS ? 'Syncing...' : 'Sync KRS'}</span>
            <p>Sinkronkan KRS Mahasiswa</p>
          </button>

          <button 
            className="action-btn"
            onClick={() => setShowKHSSyncFilter(true)}
            disabled={syncingKHS || !config?.url}
          >
            <FiDatabase size={24} />
            <span>{syncingKHS ? 'Syncing...' : 'Sync KHS'}</span>
            <p>Sinkronkan KHS/Nilai Mahasiswa</p>
          </button>

          <button 
            className="action-btn"
            onClick={() => setShowAKMSyncFilter(true)}
            disabled={syncingAKM || !config?.url}
          >
            <FiDatabase size={24} />
            <span>{syncingAKM ? 'Syncing...' : 'Sync AKM'}</span>
            <p>Sinkronkan Aktivitas Kuliah Mahasiswa</p>
          </button>
        </div>
      </div>

      {/* Biodata Mahasiswa Sync Filter Modal */}
      {showBiodataSyncFilter && (
        <div className="card">
          <h3>Sinkronisasi Biodata Mahasiswa</h3>
          <p className="text-muted">Sinkronkan biodata lengkap mahasiswa dari NEO Feeder menggunakan GetBiodataMahasiswa</p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSyncBiodata(); }}>
            <div className="form-group">
              <label>ID Registrasi Mahasiswa (Opsional)</label>
              <input
                type="text"
                value={biodataSyncFilter.id_registrasi_mahasiswa}
                onChange={(e) => setBiodataSyncFilter({ ...biodataSyncFilter, id_registrasi_mahasiswa: e.target.value })}
                className="form-control"
                placeholder="Kosongkan untuk semua mahasiswa"
              />
              <small className="text-muted">Filter untuk mahasiswa tertentu</small>
            </div>

            <div className="form-group">
              <label>Maksimal Data</label>
              <input
                type="number"
                value={biodataSyncFilter.limit}
                onChange={(e) => setBiodataSyncFilter({ ...biodataSyncFilter, limit: e.target.value })}
                className="form-control"
                min="1"
                max="1000"
                required
              />
              <small className="text-muted">Maksimal 1000 data per sinkronisasi</small>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => {
                  setShowBiodataSyncFilter(false);
                  setBiodataSyncFilter({ id_registrasi_mahasiswa: '', limit: 100 });
                }}
              >
                Batal
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={syncingBiodata}
              >
                {syncingBiodata ? 'Menyinkronkan...' : 'Mulai Sinkronisasi'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* KRS Sync Filter Modal */}
      {showKRSSyncFilter && (
        <div className="card">
          <h3>Sinkronisasi KRS Mahasiswa</h3>
          <p className="text-muted">Sinkronkan data KRS (Kartu Rencana Studi) mahasiswa dari NEO Feeder menggunakan GetKRSMahasiswa</p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSyncKRS(); }}>
            <div className="form-row">
              <div className="form-group">
                <label>ID Semester (Opsional)</label>
                <input
                  type="text"
                  value={krsSyncFilter.id_semester}
                  onChange={(e) => setKrsSyncFilter({ ...krsSyncFilter, id_semester: e.target.value })}
                  className="form-control"
                  placeholder="Contoh: 20241"
                />
                <small className="text-muted">Format: YYYYS (20241 = 2024 semester 1)</small>
              </div>

              <div className="form-group">
                <label>ID Registrasi Mahasiswa (Opsional)</label>
                <input
                  type="text"
                  value={krsSyncFilter.id_registrasi_mahasiswa}
                  onChange={(e) => setKrsSyncFilter({ ...krsSyncFilter, id_registrasi_mahasiswa: e.target.value })}
                  className="form-control"
                  placeholder="Kosongkan untuk semua mahasiswa"
                />
                <small className="text-muted">Filter untuk mahasiswa tertentu</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  value={krsSyncFilter.syncMode}
                  onChange={(e) => setKrsSyncFilter({ ...krsSyncFilter, syncMode: e.target.value })}
                  className="form-control"
                  required
                >
                  <option value="upsert">UPSERT - Update jika ada, Insert jika tidak ada</option>
                  <option value="insert">INSERT - Hanya tambah data baru, skip yang sudah ada</option>
                  <option value="update">UPDATE - Hanya update data yang sudah ada</option>
                </select>
                <small className="text-muted">
                  {krsSyncFilter.syncMode === 'upsert' && '✓ Cocok untuk sinkronisasi umum (default)'}
                  {krsSyncFilter.syncMode === 'insert' && '✓ Cocok untuk menambah KRS baru saja'}
                  {krsSyncFilter.syncMode === 'update' && '✓ Cocok untuk update KRS yang sudah ada'}
                </small>
              </div>

              <div className="form-group">
                <label>Maksimal Data</label>
                <input
                  type="number"
                  value={krsSyncFilter.limit}
                  onChange={(e) => setKrsSyncFilter({ ...krsSyncFilter, limit: e.target.value })}
                  className="form-control"
                  min="1"
                  max="1000"
                  required
                />
                <small className="text-muted">Maksimal 1000 data per sinkronisasi</small>
              </div>
            </div>

            <div className="alert alert-info">
              <strong>ℹ️ Catatan:</strong> 
              <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                <li>GetKRSMahasiswa mengambil data KRS dari NEO Feeder</li>
                <li>Data meliputi: Mata kuliah yang diambil, SKS, dan status KRS</li>
                <li>Kosongkan filter untuk mengambil semua data KRS</li>
              </ul>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => {
                  setShowKRSSyncFilter(false);
                  setKrsSyncFilter({ id_semester: '', id_registrasi_mahasiswa: '', limit: 100, syncMode: 'upsert' });
                }}
              >
                Batal
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={syncingKRS}
              >
                {syncingKRS ? 'Menyinkronkan...' : 'Mulai Sinkronisasi'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* KHS Sync Filter Modal */}
      {showKHSSyncFilter && (
        <div className="card">
          <h3>Sinkronisasi KHS (Kartu Hasil Studi)</h3>
          <p className="text-muted">Sinkronkan data nilai dan KHS mahasiswa dari NEO Feeder menggunakan GetRiwayatNilaiMahasiswa</p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSyncKHS(); }}>
            <div className="form-row">
              <div className="form-group">
                <label>ID Semester (Opsional)</label>
                <input
                  type="text"
                  value={khsSyncFilter.id_semester}
                  onChange={(e) => setKhsSyncFilter({ ...khsSyncFilter, id_semester: e.target.value })}
                  className="form-control"
                  placeholder="Contoh: 20241"
                />
                <small className="text-muted">Format: YYYYS (20241 = 2024/2025 Ganjil)</small>
              </div>

              <div className="form-group">
                <label>Program Studi (Opsional)</label>
                <select
                  value={khsSyncFilter.id_prodi}
                  onChange={(e) => setKhsSyncFilter({ ...khsSyncFilter, id_prodi: e.target.value })}
                  className="form-control"
                >
                  <option value="">Semua Program Studi</option>
                  {prodiList.map(prodi => (
                    <option key={prodi.id} value={prodi.id}>
                      {prodi.nama}
                    </option>
                  ))}
                </select>
                <small className="text-muted">Filter berdasarkan program studi</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>NIM Mahasiswa (Opsional)</label>
                <input
                  type="text"
                  value={khsSyncFilter.nim}
                  onChange={(e) => setKhsSyncFilter({ ...khsSyncFilter, nim: e.target.value })}
                  className="form-control"
                  placeholder="Kosongkan untuk semua mahasiswa"
                />
                <small className="text-muted">Filter untuk mahasiswa tertentu</small>
              </div>

              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  value={khsSyncFilter.syncMode}
                  onChange={(e) => setKhsSyncFilter({ ...khsSyncFilter, syncMode: e.target.value })}
                  className="form-control"
                  required
                >
                  <option value="upsert">UPSERT - Update jika ada, Insert jika tidak ada</option>
                  <option value="insert">INSERT - Hanya tambah data baru, skip yang sudah ada</option>
                  <option value="update">UPDATE - Hanya update data yang sudah ada</option>
                </select>
                <small className="text-muted">
                  {khsSyncFilter.syncMode === 'upsert' && '✓ Cocok untuk sinkronisasi umum (default)'}
                  {khsSyncFilter.syncMode === 'insert' && '✓ Cocok untuk menambah KHS baru saja'}
                  {khsSyncFilter.syncMode === 'update' && '✓ Cocok untuk update KHS yang sudah ada'}
                </small>
              </div>
            </div>

            <div className="alert alert-info">
              <strong>ℹ️ Catatan:</strong> 
              <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                <li>GetRiwayatNilaiMahasiswa mengambil data nilai mahasiswa dari NEO Feeder</li>
                <li>Data meliputi: Nilai angka, nilai huruf, nilai indeks/mutu</li>
                <li>Kosongkan filter untuk mengambil semua data KHS</li>
                <li>IPK dan IPS akan dihitung otomatis dari data yang disinkronkan</li>
              </ul>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => {
                  setShowKHSSyncFilter(false);
                  setKhsSyncFilter({ id_semester: '', id_prodi: '', nim: '', syncMode: 'upsert' });
                }}
              >
                Batal
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={syncingKHS}
              >
                {syncingKHS ? 'Menyinkronkan...' : 'Mulai Sinkronisasi'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* AKM Sync Filter Modal */}
      {showAKMSyncFilter && (
        <div className="card">
          <h3>Sinkronisasi AKM (Aktivitas Kuliah Mahasiswa)</h3>
          <p className="text-muted">Sinkronkan data aktivitas kuliah mahasiswa dari NEO Feeder menggunakan GetAktivitasKuliahMahasiswa</p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSyncAKM(); }}>
            <div className="form-row">
              <div className="form-group">
                <label>Semester</label>
                <input
                  type="text"
                  value={akmSyncFilter.id_semester}
                  onChange={(e) => setAkmSyncFilter({ ...akmSyncFilter, id_semester: e.target.value })}
                  className="form-control"
                  placeholder="Contoh: 20241"
                />
                <small className="text-muted">Filter berdasarkan semester</small>
              </div>

              <div className="form-group">
                <label>Program Studi (Opsional)</label>
                <select
                  value={akmSyncFilter.id_prodi}
                  onChange={(e) => setAkmSyncFilter({ ...akmSyncFilter, id_prodi: e.target.value })}
                  className="form-control"
                >
                  <option value="">Semua Program Studi</option>
                  {prodiList.map(prodi => (
                    <option key={prodi.id} value={prodi.id}>
                      {prodi.nama}
                    </option>
                  ))}
                </select>
                <small className="text-muted">Filter berdasarkan program studi</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>NIM Mahasiswa (Opsional)</label>
                <input
                  type="text"
                  value={akmSyncFilter.nim}
                  onChange={(e) => setAkmSyncFilter({ ...akmSyncFilter, nim: e.target.value })}
                  className="form-control"
                  placeholder="Kosongkan untuk semua mahasiswa"
                />
                <small className="text-muted">Filter untuk mahasiswa tertentu</small>
              </div>

              <div className="form-group">
                <label>Jumlah Request</label>
                <select
                  value={akmSyncFilter.limitType}
                  onChange={(e) => setAkmSyncFilter({ ...akmSyncFilter, limitType: e.target.value })}
                  className="form-control"
                  required
                >
                  <option value="custom">Jumlah Tertentu</option>
                  <option value="all">Semua Data</option>
                </select>
                <small className="text-muted">Pilih semua data atau batasi jumlah request</small>
              </div>
            </div>

            {akmSyncFilter.limitType === 'custom' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Limit per Request</label>
                  <input
                    type="number"
                    value={akmSyncFilter.limit}
                    onChange={(e) => setAkmSyncFilter({ ...akmSyncFilter, limit: e.target.value })}
                    className="form-control"
                    min="1"
                    max="10000"
                    required
                  />
                  <small className="text-muted">Jumlah maksimal data per request (1-10000)</small>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  value={akmSyncFilter.syncMode}
                  onChange={(e) => setAkmSyncFilter({ ...akmSyncFilter, syncMode: e.target.value })}
                  className="form-control"
                  required
                >
                  <option value="upsert">UPSERT - Update jika ada, Insert jika tidak ada</option>
                  <option value="insert">INSERT - Hanya tambah data baru, skip yang sudah ada</option>
                  <option value="update">UPDATE - Hanya update data yang sudah ada</option>
                </select>
                <small className="text-muted">
                  {akmSyncFilter.syncMode === 'upsert' && '✓ Cocok untuk sinkronisasi umum (default)'}
                  {akmSyncFilter.syncMode === 'insert' && '✓ Cocok untuk menambah AKM baru saja'}
                  {akmSyncFilter.syncMode === 'update' && '✓ Cocok untuk update AKM yang sudah ada'}
                </small>
              </div>
            </div>

            <div className="alert alert-info">
              <strong>ℹ️ Catatan:</strong> 
              <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                <li>GetAktivitasKuliahMahasiswa mengambil data aktivitas kuliah dari NEO Feeder</li>
                <li>Data meliputi: Status mahasiswa, IPS, IPK, SKS, biaya kuliah per semester</li>
                <li>Kosongkan filter untuk mengambil semua data AKM</li>
                <li>Data ini menunjukkan aktivitas dan status mahasiswa per semester</li>
                <li><strong>Jumlah Request:</strong> Pilih "Semua Data" untuk sync semua AKM atau tentukan limit khusus</li>
              </ul>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => {
                  setShowAKMSyncFilter(false);
                  setAkmSyncFilter({ id_semester: '', id_prodi: '', nim: '', limit: 500, limitType: 'custom', syncMode: 'upsert' });
                }}
              >
                Batal
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={syncingAKM}
              >
                {syncingAKM ? 'Menyinkronkan...' : 'Mulai Sinkronisasi'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sync History */}
      <div className="card">
        <h3>Riwayat Sinkronisasi</h3>
        {history.length === 0 ? (
          <div className="empty-state">
            <p>Belum ada riwayat sinkronisasi</p>
          </div>
        ) : (
          <div className="modern-table-container">
            <table className="modern-table accent-green">
              <thead>
                <tr>
                  <th>Waktu</th>
                  <th>Tipe</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Berhasil</th>
                  <th>Gagal</th>
                  <th>Durasi</th>
                  <th>User</th>
                </tr>
              </thead>
              <tbody>
                {history.map((log) => (
                  <tr key={log._id}>
                    <td>{new Date(log.createdAt).toLocaleString('id-ID')}</td>
                    <td>
                      <span className="table-badge table-badge-info">
                        {log.syncType}
                      </span>
                    </td>
                    <td>
                      <span className={`table-badge ${getStatusBadge(log.status)}`}>
                        {getStatusIcon(log.status)} {log.status}
                      </span>
                    </td>
                    <td><strong>{log.totalRecords}</strong></td>
                    <td>
                      <span className="table-badge table-badge-success">
                        {log.successRecords}
                      </span>
                    </td>
                    <td>
                      <span className="table-badge table-badge-danger">
                        {log.failedRecords}
                      </span>
                    </td>
                    <td>
                      {log.endTime && log.startTime
                        ? `${Math.round((new Date(log.endTime) - new Date(log.startTime)) / 1000)}s`
                        : '-'
                      }
                    </td>
                    <td>{log.performedBy?.name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default NeoFeeder;
